import { AddressObject } from './Address';
import { OpenHouseObject } from './OpenHouse';
import { SimpleListing, SimpleListingObject } from './SimpleListing';
import { RepliersListingTransactionType } from './repliers/RepliersListingTransactionType';
import { RepliersListingStatus } from './repliers/RepliersListingStatus';
import { RepliersListingLastStatus } from './repliers/RepliersListingLastStatus';
import { Place } from './Place';
import { PlaceType } from './PlaceType';
import { RepliersListingBuildingClass } from './repliers/RepliersListingBuildingClass';
import isDefinedAndNonNull from '../utils/isDefinedAndNonNull';

export type PropertySnapshotRecord = {
  propertyId: string;
  listingId: string;
  mlsNumber: string;
  listDate: string;
  listPrice: number;
  soldDate: null | string;
  soldPrice: null | number;
  latitude: number;
  longitude: number;
  images: string[];
  blurhashes: string[];
  numBathrooms: null | number;
  numBathroomsPlus: null | number;
  numBedrooms: null | number;
  numBedroomsPlus: null | number;
  numParkingSpaces: null | number;
  numGarageSpaces: null | number;
  sqft: null | string;
  virtualTourUrl: null | string;
  address: AddressObject;
  brokerageName: string;
  transactionType: RepliersListingTransactionType;
  buildingClass: RepliersListingBuildingClass;
  status: RepliersListingStatus;
  lastStatus: null | RepliersListingLastStatus;
  permissionsCanDisplayAddress: boolean;
  permissionsCanDisplayPublic: boolean;
  daysOnMarketFromMls: null | number;
  mlsUpdatedOn: string;
  resource: string;
  openHouses: null | Array<OpenHouseObject>;
  offerDate: null | string;
  offerCount: null | number;
  isFsbo: boolean;
  isPowerOfSale: null | boolean;
};

export type PropertySnapshotObject = {
  propertyId: string;
  latitude: number;
  longitude: number;
  listing: SimpleListingObject;
};

export class PropertySnapshot {
  propertyId: string;
  listing: SimpleListing;
  latitude: number;
  longitude: number;

  static fromRecord({
    propertyId,
    listingId,
    mlsNumber,
    lastStatus,
    listDate,
    soldDate,
    listPrice,
    soldPrice,
    latitude,
    longitude,
    images,
    blurhashes,
    numBathrooms,
    numBathroomsPlus,
    numBedrooms,
    numBedroomsPlus,
    numParkingSpaces,
    numGarageSpaces,
    sqft,
    virtualTourUrl,
    address,
    brokerageName,
    transactionType,
    buildingClass,
    status,
    permissionsCanDisplayAddress,
    permissionsCanDisplayPublic,
    daysOnMarketFromMls,
    mlsUpdatedOn,
    resource,
    openHouses,
    offerDate,
    offerCount,
    isFsbo,
    isPowerOfSale,
  }: PropertySnapshotRecord): PropertySnapshot {
    return new PropertySnapshot({
      propertyId,
      longitude,
      latitude,
      listing: {
        id: listingId,
        mlsNumber,
        listDate,
        listPrice,
        soldDate,
        soldPrice,
        images,
        blurhashes,
        details: {
          numBathrooms,
          numBathroomsPlus,
          numBedrooms,
          numBedroomsPlus,
          numParkingSpaces,
          numGarageSpaces,
          sqft,
          virtualTourUrl,
        },
        address,
        coordinates: {
          latitude,
          longitude,
        },
        brokerageName,
        transactionType,
        buildingClass,
        status,
        lastStatus,
        permissions: {
          canDisplayAddress: permissionsCanDisplayAddress,
          canDisplayPublic: permissionsCanDisplayPublic,
        },
        daysOnMarketFromMls,
        mlsUpdatedOn,
        resource,
        openHouses,
        offerDate,
        offerCount,
        isFsbo,
        isPowerOfSale,
      },
    });
  }

  static fromObject(propertySnapshotObject: PropertySnapshotObject) {
    return new PropertySnapshot(propertySnapshotObject);
  }

  constructor({
    propertyId,
    longitude,
    latitude,
    listing,
  }: PropertySnapshotObject) {
    this.propertyId = propertyId;
    this.latitude = latitude;
    this.longitude = longitude;
    this.listing = SimpleListing.fromObject(listing);
  }

  locationSignatures(): Set<string> {
    const signatures = [
      this.locationZipSignature(),
      this.locationCoordinatesSignature(),
      this.locationAddressSignature(),
    ].filter(sig => isDefinedAndNonNull(sig));
    return new Set(signatures);
  }

  locationZipSignature(): string {
    return this.listing.address.locationZipSignature();
  }

  locationCoordinatesSignature(): string {
    return `${this.latitude} ${this.longitude}`;
  }

  locationAddressSignature(): string {
    return this.listing.address.toLocationString();
  }

  toPlace(): Place {
    const address = this.listing.address;

    return Place.fromObject({
      type: PlaceType.Address,
      name: `${address.toString({
        city: true,
        abbreviatedState: true,
      })}, ${address.zip}`, // Note: the zip/postal code needs to be here because we rely on it sometimes for reverse lookups
      center: [this.longitude, this.latitude],
    });
  }

  toObject(): PropertySnapshotObject {
    return {
      propertyId: this.propertyId,
      listing: this.listing.toObject(),
      latitude: this.latitude,
      longitude: this.longitude,
    };
  }
}
